import { enumMembers, type ApiSchema } from "./apiSchema";
import { requestJson, withQuery } from "./http/httpClient";
import {
  readArray,
  readBoolean,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "./http/jsonDecoding";
import { readStringList } from "./content/contentDecoders";
import { decodePerson, type HistoryPerson } from "./people";
import { PROJECT_RULE_NAMES, type ProjectRuleName } from "./settingsApi";
import { PROJECT_ROLES, type ProjectRole } from "./projectsApi";

/**
 * Which project permissions existed when (Q-67): changes of groups, their sets, members,
 * inclusions and order (GS-1 to GS-3), the grants of before the switch, and rule changes with
 * their reason (ST-1), for those who manage the project's people. Routes:
 * `server/src/dmw/api/routes/accounts/permission_history.py`.
 */

export type PermissionChange = ApiSchema<"PermissionChange">;

export const PERMISSION_CHANGES = enumMembers<PermissionChange>({
  membership_granted: true,
  project_role_changed: true,
  membership_revoked: true,
  curation_granted: true,
  curation_revoked: true,
  project_settings_changed: true,
  group_role_granted: true,
  group_role_changed: true,
  group_role_revoked: true,
  group_curation_changed: true,
  project_deleted: true,
  project_restored: true,
  project_purged: true,
  group_made: true,
  group_changed: true,
  group_removed: true,
  group_grants_changed: true,
  group_member_added: true,
  group_member_removed: true,
  group_included: true,
  group_overrides_changed: true,
  group_excluded: true,
  group_order_changed: true,
});

export interface PermissionEvent {
  readonly eventId: string;
  readonly change: PermissionChange;
  /** Whose permission changed; `null` for a rule change, which concerns everyone. */
  readonly person: HistoryPerson | null;
  readonly role: ProjectRole | null;
  readonly itemKind: string | null;
  /** `null` with an item kind: every item of that kind. */
  readonly itemId: string | null;
  /** `null` for an event rebuilt from records older than the history. */
  readonly actor: HistoryPerson | null;
  /** ISO 8601. */
  readonly at: string;
  readonly reconstructed: boolean;
  /** For a rules record: the rules it set or changed, and why (empty for the first). */
  readonly rules: { readonly changed: readonly ProjectRuleName[]; readonly reason: string } | null;
  /** Why, for a change that needed a reason: switching on the server's keys (GS-4). */
  readonly reason: string | null;
  /** For a group's role (F18) or curation (F133): the group, named as it was then. */
  readonly group: { readonly groupId: string; readonly name: string } | null;
  /** For a group's curation: the kinds it curates from then on; empty once taken. */
  readonly curatedKinds: readonly string[] | null;
}

const HISTORY_LIMIT = 200;

/** The project's newest events, oldest first. */
export function listPermissionHistory(projectId: string, signal?: AbortSignal): Promise<readonly PermissionEvent[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/permission-history`, { limit: HISTORY_LIMIT }),
    signal,
    decodeResponse: (body) => readArray(body, "permission history", (item) => decodeEvent(readObject(item, "event"))),
  });
}

function decodeEvent(event: JsonObject): PermissionEvent {
  const role = readNullableString(event, "role");
  const changedRules = event["changed_rules"];
  return {
    eventId: readString(event, "id"),
    change: readOneOf(event, "change", PERMISSION_CHANGES),
    person: event["user"] === null ? null : decodePerson(readObject(event["user"], "user")),
    role: role === null ? null : readOneOf(event, "role", PROJECT_ROLES),
    itemKind: readNullableString(event, "item_kind"),
    itemId: readNullableString(event, "item_id"),
    actor: event["actor"] === null ? null : decodePerson(readObject(event["actor"], "actor")),
    at: readString(event, "at"),
    reconstructed: readBoolean(event, "reconstructed"),
    rules:
      changedRules === null || changedRules === undefined
        ? null
        : {
            changed: readArray(changedRules, "changed rules", (name) => readOneOf({ name }, "name", PROJECT_RULE_NAMES)),
            reason: readNullableString(event, "reason") ?? "",
          },
    reason: readNullableString(event, "reason"),
    group: decodeGroup(event["group"]),
    curatedKinds: event["curated_kinds"] === null || event["curated_kinds"] === undefined ? null : readStringList(event, "curated_kinds"),
  };
}

function decodeGroup(value: unknown): PermissionEvent["group"] {
  if (value === null || value === undefined) return null;
  const group = readObject(value, "group");
  return { groupId: readString(group, "group_id"), name: readString(group, "name") };
}
